// import { Flex } from '#UNSAFE_Flex';
import { useTestId, type TestIdProps } from '#hooks/UNSAFE_useTestId';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { colorPickerPropVars } from './themes/ColorPickerContract.css';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { ColorPickerRedwoodTheme } from './themes/redwood/ColorPickerTheme';
import { ColorPalette } from '#UNSAFE_ColorPalette';
import { Size } from '#utils/UNSAFE_size';
import { ComponentProps } from 'preact';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { type ColorValue } from '#utils/UNSAFE_color';
import { Flex } from '#UNSAFE_Flex';
import { isMobile } from '#utils/UNSAFE_clientUtils';

type ColorPaletteProps = ComponentProps<typeof ColorPalette>;
type PickedPropsFromColorPalette = Pick<ColorPaletteProps, 'swatchSize' | 'palette'>;

type Props = TestIdProps &
  PickedPropsFromColorPalette & {
    /**
     * The selected color
     */
    value?: ColorValue;

    /**
     * When property is set to "auto", the color picker will automatically adjust to the height of the palette.
     * This is mainly to be used in conjunction with InputColor.
     * <p>If not specified, defaults to the theme specified height.<br/>
     */
    height?: 'auto';

    /**
     * Specifies the component style maxWidth.
     * <p>If not specified, defaults to the theme specified maxWidth.<br/>
     * This attribute accepts values of type:<br/>
     * 0 | `${number}${CssUnits}` | `${number}x` | `--${string}` | `var($string})` | `${number}%`</p>
     * <p>When this component is used in composition and the component should take up 100% of the parent container's
     * width, set maxWidth to '100%'.<br/>
     * Because there is a theme default value for maxWidth, when setting the width property to achieve a fixed width,
     * set the maxWidth property to the same value.</p>
     */
    maxWidth?: Size;

    /**
     * Specifies the component style width.
     * <p>If not specified, defaults to '100%'.</p>
     * <p>This attribute accepts values of type:</p>
     * <p>0 | `${number}${CssUnits}` | `${number}x` | `--${string}`  | `var($string})` | `${number}%`</p>
     * <br/>
     * <p>Because there is a theme default value for maxWidth, when setting the width property to achieve a fixed width,
     * set the maxWidth property to the same value.</p>
     */
    width?: Size;

    /**
     * Callback invoked when the user selects a color in the Palette. This callback is called even when the previous
     * value and the value are the same, that is, when the user selects an already selected color.
     * The app needs to handle this to write back the controlled 'value' property, or else no color will be shown
     * to be selected.
     */
    onCommit: (detail: ValueUpdateDetail<ColorValue>) => void;
  };

/**
 * A ColorPicker is a component that allows users to select a single color.
 */
export const ColorPicker = forwardRef(
  (
    { swatchSize = 'xs', palette, height, maxWidth, value, testId, width, onCommit }: Props,
    pickerRef?: ForwardedRef<HTMLDivElement>
  ) => {
    // we need to render the baseTheme on the root dom element so that the CSS vars in colorPickerPropVars will be defined.
    const { classes } = useComponentTheme(ColorPickerRedwoodTheme);

    const testIdProps = useTestId(testId);

    return (
      <div ref={pickerRef} class={classes} {...testIdProps}>
        <Flex
          direction="column"
          height={
            height === 'auto' ? undefined : isMobile() ? undefined : colorPickerPropVars.maxHeight
          }
          maxWidth={
            maxWidth !== undefined ? maxWidth : isMobile() ? '100%' : colorPickerPropVars.maxWidth
          }
          width={width !== undefined ? width : colorPickerPropVars.width}>
          <ColorPalette
            palette={palette}
            swatchSize={swatchSize}
            value={value}
            testId={testId ? testId + '_picker' : undefined}
            onCommit={onCommit}></ColorPalette>
        </Flex>
      </div>
    );
  }
);
